// The optimistic click path (SPEC §3, "Optimistic client").
//
// Render the click instantly, reconcile against the next frame. At a 500 ms tick
// nobody perceives a queue, and this is what makes a batched backend feel live —
// it is not optional.
//
// Reconciliation has three FAILURE outcomes and they must look different, which
// is the whole reason SELF carries a verdict:
//
//	illegal  adjacency, no budget, no country, frozen — flashes and RETURNS the
//	         click to the bank
//	lost     someone else landed the killing blow — snaps back and reads as
//	         "someone beat you to it". NOT refunded: the click was spent on a
//	         pixel that changed hands under it.
//	dropped  never reached the board — refunded, and says so
//
// If all three are the same flash a player cannot tell an illegal move from a
// lost one, so `Outcome` keeps them apart all the way to the renderer, which
// draws a different colour, shape and duration for each.
//
// This module deliberately does NOT write predicted state into BoardReplica. The
// replica is the authoritative copy and a delta may land on the same pixel in the
// same tick; an overlay that the renderer composites on top reconciles cleanly
// and can never leave a wrong pixel behind if a verdict goes missing.
//
// The bank is predicted locally and CORRECTED by SELF, never the other way round.
// SELF is polled every hub.SelfEveryTicks (~2 s) even when nothing happened, so a
// prediction that drifts — a click the server refunded for a reason this client
// did not model — self-heals within a couple of seconds without any reconcile
// logic of its own.

import { ClickAttack, ClickBeachhead, ClickFortify, type ClickKind, type PixelID } from '../wire/core';
import {
  VerdictApplied,
  VerdictCaptured,
  VerdictDropped,
  VerdictFrozen,
  VerdictIllegalAdjacency,
  VerdictLostRace,
  VerdictNoBudget,
  VerdictNoCountry,
  verdictRefunds,
  type Verdict,
} from '../wire/core';
import type { Self } from '../wire/self';

/** Drawn the moment the player clicks, before the server has said anything. */
export const OutcomePending = 'pending';
/** The click landed and the damage stands. */
export const OutcomeApplied = 'applied';
/** The killing blow: this click flipped the pixel. */
export const OutcomeCaptured = 'captured';
/** Refused: adjacency, no budget, no country, or the season freeze. Refunded. */
export const OutcomeIllegal = 'illegal';
/** Someone beat you to it. Snaps back. NOT refunded. */
export const OutcomeLost = 'lost';
/** Never reached the board. Refunded, and says so. */
export const OutcomeDropped = 'dropped';
/**
 * Superseded by a newer verdict in the same tick, or aged out with no verdict at
 * all. The click's effect is whatever the board says; there is nothing to flash.
 */
export const OutcomeSettled = 'settled';

export type Outcome =
  | typeof OutcomePending
  | typeof OutcomeApplied
  | typeof OutcomeCaptured
  | typeof OutcomeIllegal
  | typeof OutcomeLost
  | typeof OutcomeDropped
  | typeof OutcomeSettled;

/** Whether an outcome is one of the three failures §3 requires to look different. */
export function isFailure(o: Outcome): boolean {
  return o === OutcomeIllegal || o === OutcomeLost || o === OutcomeDropped;
}

/** One click the player made, and where it has got to. */
export interface OptimisticClick {
  seq: number;
  target: PixelID;
  kind: ClickKind;
  /** The country the client drew it for, so a late verdict is still attributable. */
  country: number;
  outcome: Outcome;
  /** When the player clicked. */
  bornAt: number;
  /** When the outcome was decided, or 0 while pending. */
  resolvedAt: number;
  /** The verdict SELF carried, or null if it was never judged. */
  verdict: Verdict | null;
}

export interface BankState {
  banked: number;
  capacity: number;
  /** Whole seconds to the next banked click, counted down locally between SELF frames. */
  refillSeconds: number;
}

/** Why a click was refused before it ever reached the socket. */
export const RefuseNoBudget = 'no-budget';
export const RefuseNoCountry = 'no-country';
export const RefuseOffBoard = 'off-board';
export const RefuseNotAdjacent = 'not-adjacent';
export type Refusal = typeof RefuseNoBudget | typeof RefuseNoCountry | typeof RefuseOffBoard | typeof RefuseNotAdjacent;

export interface ClickBookOptions {
  now?: () => number;
  /**
   * How long a resolved click stays in the list so the renderer can animate it
   * out. §15's tracer is ~600 ms and its arc fades over ~1.2 s.
   */
  holdMs?: number;
  /**
   * How long a pending click waits for a verdict before it is settled. SELF is
   * sent on change and polled every ~2 s, so anything past a few ticks means the
   * verdict was superseded or the frame was shed.
   */
  verdictTimeoutMs?: number;
}

/**
 * The client's own click ledger: sequence numbers, the predicted bank, the
 * pending clicks, and the outcomes the renderer animates.
 */
export class ClickBook {
  private readonly now: () => number;
  private readonly holdMs: number;
  private readonly verdictTimeoutMs: number;

  private seq = 0;
  private clicks: OptimisticClick[] = [];

  private bank: BankState = { banked: 0, capacity: 0, refillSeconds: 0 };
  /** When the bank was last set from SELF, so refillSeconds can be counted down locally. */
  private bankAt = 0;
  private haveBank = false;

  constructor(opts: ClickBookOptions = {}) {
    this.now = opts.now ?? (() => Date.now());
    this.holdMs = opts.holdMs ?? 1800;
    this.verdictTimeoutMs = opts.verdictTimeoutMs ?? 3000;
  }

  /** The predicted bank, with the refill counted down since the last SELF. */
  bankState(): BankState {
    if (!this.haveBank) return { ...this.bank };
    const elapsed = Math.floor((this.now() - this.bankAt) / 1000);
    const refill = Math.max(0, this.bank.refillSeconds - elapsed);
    return { banked: this.bank.banked, capacity: this.bank.capacity, refillSeconds: refill };
  }

  /** Whether SELF has ever arrived. Before it has, the bank is unknown, not zero. */
  bankKnown(): boolean {
    return this.haveBank;
  }

  /** Every click still worth drawing: pending ones, and resolved ones inside the hold window. */
  active(): readonly OptimisticClick[] {
    return this.clicks;
  }

  /** The next sequence number, without consuming it. */
  nextSeq(): number {
    return this.seq + 1;
  }

  /**
   * Records a click the player just made and spends one from the predicted bank.
   *
   * Returns the click, which the caller both sends as CLICK and hands to the
   * renderer. The overlay draws it on the very next frame; nothing waits for the
   * server.
   */
  intend(target: PixelID, kind: ClickKind, country: number): OptimisticClick {
    const t = this.now();
    const c: OptimisticClick = {
      seq: ++this.seq,
      target,
      kind,
      country,
      outcome: OutcomePending,
      bornAt: t,
      resolvedAt: 0,
      verdict: null,
    };
    if (this.haveBank && this.bank.banked > 0) this.bank.banked--;
    this.clicks.push(c);
    return c;
  }

  /**
   * Applies a SELF frame: the authoritative bank, and the verdict on at most one
   * click.
   *
   * The hub sends ONE SELF per tick per connection and the last verdict of the
   * tick wins, so a client that clicked three times in one tick is told about the
   * newest one only. Older pending clicks are settled rather than left waiting:
   * their effect is already in the board, and a marker that never resolves is a
   * marker that never stops animating.
   */
  applySelf(s: Self): OptimisticClick | null {
    const t = this.now();
    this.bank = { banked: s.banked, capacity: s.capacity, refillSeconds: s.refillSeconds };
    this.bankAt = t;
    this.haveBank = true;

    let resolved: OptimisticClick | null = null;
    if (s.hasLast) {
      for (const c of this.clicks) {
        if (c.outcome !== OutcomePending) continue;
        if (c.seq === s.seq) {
          c.outcome = outcomeFor(s.verdict);
          c.verdict = s.verdict;
          c.resolvedAt = t;
          resolved = c;
        } else if (c.seq < s.seq) {
          // Superseded within the tick. Its damage is in the delta; there is no
          // verdict for it and nothing to flash.
          c.outcome = OutcomeSettled;
          c.resolvedAt = t;
        }
      }
    }
    this.expire(t);
    return resolved;
  }

  /**
   * Drops resolved clicks past the hold window and settles pending ones that were
   * never judged. Call it once a frame; applySelf calls it too.
   */
  expire(now = this.now()): void {
    let write = 0;
    for (let i = 0; i < this.clicks.length; i++) {
      const c = this.clicks[i]!;
      if (c.outcome === OutcomePending) {
        if (now - c.bornAt > this.verdictTimeoutMs) {
          c.outcome = OutcomeSettled;
          c.resolvedAt = now;
        } else {
          this.clicks[write++] = c;
          continue;
        }
      }
      if (now - c.resolvedAt <= this.holdMs) this.clicks[write++] = c;
    }
    this.clicks.length = write;
  }

  /**
   * The advisory local check, in the same spirit as the server's: what can be
   * settled without reading state the tick is concurrently writing is settled
   * here, so the common refusals never cost a round trip. It is ADVISORY — the
   * authoritative answer is still the verdict on SELF.
   */
  refuse(
    target: PixelID,
    kind: ClickKind,
    country: number,
    adjacent: (p: PixelID, c: number) => boolean,
    onBoard: (p: PixelID) => boolean,
  ): Refusal | null {
    if (!onBoard(target)) return RefuseOffBoard;
    if (country === 0) return RefuseNoCountry;
    if (this.haveBank && this.bank.banked <= 0) return RefuseNoBudget;
    if (kind === ClickBeachhead) return null; // beachhead ignores adjacency by design
    if (kind === ClickAttack || kind === ClickFortify) {
      if (!adjacent(target, country)) return RefuseNotAdjacent;
    }
    return null;
  }

  /** Forgets everything but the bank, for a reconnect. */
  reset(): void {
    this.clicks = [];
  }
}

/** Maps a server verdict onto what the player is shown. */
export function outcomeFor(v: Verdict): Outcome {
  switch (v) {
    case VerdictApplied:
      return OutcomeApplied;
    case VerdictCaptured:
      return OutcomeCaptured;
    case VerdictIllegalAdjacency:
    case VerdictNoBudget:
    case VerdictNoCountry:
    case VerdictFrozen:
      return OutcomeIllegal;
    case VerdictLostRace:
      return OutcomeLost;
    case VerdictDropped:
      return OutcomeDropped;
    default:
      return OutcomeSettled;
  }
}

/**
 * Whether the bank gets the click back. Derived from the verdict, exactly as
 * wire.Self.Refunds derives it, so the client and the server cannot disagree
 * about whether a click was paid for.
 *
 * VerdictLostRace is the one failure that does NOT refund: the click was spent,
 * it just arrived second.
 */
export function outcomeRefunds(v: Verdict): boolean {
  return verdictRefunds(v);
}
